import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Camera, Video, Home as HomeIcon, Megaphone, Users, Code2, GraduationCap, Sparkles, Check, ArrowRight, ScrollText, Handshake } from "lucide-react";

const ROLE_ICONS = {
  makelaar: HomeIcon,
  fotograaf: Camera,
  videograaf: Video,
  marketing: Megaphone,
  accountmanager: Users,
  aankoopmakelaar: Briefcase,
  developer: Code2,
  stage: GraduationCap,
  anders: Sparkles,
};

const ROLE_DESC = {
  makelaar: "Bezichtigingen verzorgen, verkopers begeleiden en het gezicht van Woon Verder in de regio zijn.",
  fotograaf: "Woningen fotograferen met oog voor licht, sfeer en detail. Je werkt op afspraak.",
  videograaf: "Video-tours en drone-opnames maken. Bewerking + oplevering binnen 48 uur.",
  marketing: "Content, social media en groei-campagnes bedenken en uitvoeren.",
  accountmanager: "Nieuwe verkopers onboarden en klanttevredenheid bewaken.",
  aankoopmakelaar: "Kopers begeleiden bij de zoektocht en onderhandelen namens hen.",
  developer: "Meebouwen aan het platform (React/FastAPI). Remote-first.",
  stage: "Meelopen en leren in vastgoed, tech, fotografie of marketing.",
  anders: "Heb je een ander idee? Vertel ons wat jij voor Woon Verder kunt betekenen.",
};

const AVAILABILITY = [
  { v: "full-time", label: "Full-time" },
  { v: "part-time", label: "Part-time" },
  { v: "zzp", label: "ZZP / freelance" },
  { v: "oproep", label: "Oproep / per opdracht" },
];

export default function Careers() {
  const [roles, setRoles] = useState({});
  const [f, setF] = useState({
    name: "", email: "", phone: "", role: "makelaar", city: "",
    experience_years: "", availability: "zzp", portfolio_url: "",
    linkedin_url: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { http.get("/careers/roles").then((r) => setRoles(r.data.roles)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Vul naam, e-mail en motivatie in.");
    setBusy(true);
    try {
      const payload = {
        ...f,
        experience_years: f.experience_years ? parseInt(f.experience_years) : null,
      };
      const { data } = await http.post("/careers/apply", payload);
      setDone(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto py-24 px-4 text-center">
      <div className="w-16 h-16 rounded-full bg-[color:var(--wv-green)] grid place-items-center mx-auto"><Check className="w-8 h-8 text-white" /></div>
      <h1 className="font-display text-4xl font-extrabold mt-4">Sollicitatie ontvangen</h1>
      <p className="mt-3 text-slate-600">{done.message}</p>
      <p className="mt-4 font-mono-num text-lg">Referentie: <strong data-testid="career-ref">{done.reference}</strong></p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      {/* Hero */}
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[color:var(--wv-green)]/10 border border-[color:var(--wv-green)]/20 text-[color:var(--wv-green-dark)] text-xs font-bold uppercase tracking-widest">
        <Briefcase className="w-3.5 h-3.5" /> Werk bij Woon Verder
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-3" data-testid="careers-title">Bouw mee aan een eerlijker vastgoedplatform</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">Woon Verder groeit — we zoeken makelaars, fotografen, videografen, marketeers en meer talent dat vastgoed anders wil aanpakken. Werk hybride, remote of op locatie.</p>

      {/* Roles grid */}
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(roles).map(([key, label]) => {
          const Ic = ROLE_ICONS[key] || Briefcase;
          const active = f.role === key;
          return (
            <button key={key} onClick={() => setF({ ...f, role: key })} className={`text-left p-5 rounded-2xl border-2 transition-all ${active ? "border-[color:var(--wv-green)] bg-[color:var(--wv-green)]/5" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`career-role-${key}`}>
              <div className="flex items-center gap-2"><Ic className="w-5 h-5 text-[color:var(--wv-green-dark)]" /><p className="font-display font-bold">{label}</p></div>
              <p className="mt-2 text-sm text-slate-600">{ROLE_DESC[key]}</p>
            </button>
          );
        })}
      </div>

      {/* Notary partner block */}
      <div className="mt-10 p-6 rounded-2xl bg-[color:var(--wv-obsidian)] text-white grid md:grid-cols-[auto_1fr_auto] items-center gap-5">
        <div className="w-12 h-12 rounded-xl bg-[color:var(--wv-green)] grid place-items-center flex-shrink-0"><Handshake className="w-6 h-6 text-white" /></div>
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-white/60">Partnerkantoor</p>
          <p className="font-display text-xl font-extrabold mt-1">Vaste notarispartner voor kopers en verkopers</p>
          <p className="mt-2 text-sm text-white/85 max-w-2xl">Woon Verder werkt samen met een vaste notarispartner. Bij een geaccepteerd bod kunt u — als koper of verkoper — <strong>direct terecht bij ons partnerkantoor</strong> voor de koopakte en overdracht. Snelle doorlooptijd en vaste tarieven.</p>
          <p className="mt-2 text-xs text-white/60">Let op: notariskosten zijn <strong>buiten de Woon Verder verkoopfee (2,5%)</strong> — deze worden rechtstreeks door de notaris gefactureerd conform het wettelijke tarief. U bent uiteraard vrij een andere notaris te kiezen.</p>
        </div>
        <ScrollText className="w-14 h-14 text-white/20 hidden md:block" />
      </div>

      {/* Application form */}
      <form onSubmit={submit} className="mt-10 p-6 bg-white border border-slate-200 rounded-2xl">
        <p className="font-display text-2xl font-extrabold text-[color:var(--wv-obsidian)]">Solliciteer op <span className="text-[color:var(--wv-green-dark)]">{roles[f.role] || f.role}</span></p>
        <p className="text-sm text-slate-600 mt-1">Wilt u iets anders? Kies "Anders — ik heb een voorstel" en vertel het.</p>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div><label className="text-sm font-semibold">Naam *</label><input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="c-name" required /></div>
          <div><label className="text-sm font-semibold">E-mail *</label><input type="email" className="wv-input mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="c-email" required /></div>
          <div><label className="text-sm font-semibold">Telefoon</label><input className="wv-input mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="c-phone" /></div>
          <div><label className="text-sm font-semibold">Woonplaats</label><input className="wv-input mt-1" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="c-city" /></div>
          <div><label className="text-sm font-semibold">Ervaring (jaren)</label><input type="number" min="0" max="50" className="wv-input mt-1" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} data-testid="c-exp" /></div>
          <div>
            <label className="text-sm font-semibold">Beschikbaarheid</label>
            <select className="wv-input mt-1" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} data-testid="c-avail">
              {AVAILABILITY.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </select>
          </div>
          <div><label className="text-sm font-semibold">Portfolio-URL (fotografie/video/marketing)</label><input type="url" className="wv-input mt-1" placeholder="https://…" value={f.portfolio_url} onChange={(e) => setF({ ...f, portfolio_url: e.target.value })} data-testid="c-portfolio" /></div>
          <div><label className="text-sm font-semibold">LinkedIn-URL</label><input type="url" className="wv-input mt-1" placeholder="https://linkedin.com/in/…" value={f.linkedin_url} onChange={(e) => setF({ ...f, linkedin_url: e.target.value })} data-testid="c-linkedin" /></div>
          <div className="md:col-span-2">
            <label className="text-sm font-semibold">Motivatie *</label>
            <textarea className="wv-input mt-1" rows="5" placeholder="Vertel kort wat je voor Woon Verder kunt betekenen en waarom deze rol bij je past." value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="c-message" required />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">We reageren binnen 5 werkdagen. Alle gegevens worden vertrouwelijk behandeld conform ons privacybeleid.</p>
          <button type="submit" disabled={busy} className="wv-btn-green inline-flex items-center gap-2" data-testid="c-submit">
            {busy ? "Verzenden…" : "Sollicitatie versturen"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
